/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Public resources library page for Manhattan WMS. Reads resources
 * from V2 Supabase (domain='wms', is_published=true), groups by
 * category in JS, and renders the five sections in a curated order
 * (CATEGORY_ORDER) rather than alphabetic.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import ResourceCategorySection from '../../components/showcase/ResourceCategorySection';
import ShowcaseSiblingNav from '../../components/showcase/ShowcaseSiblingNav';
import v2Supabase from '../../lib/v2Supabase';

// Display order — not alphabetic. Documentation first because that's
// what most visitors come looking for; reference last because it's
// long-tail browse content.
const CATEGORY_ORDER = [
  'documentation',
  'tools',
  'community',
  'certifications',
  'reference',
];

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
      }}
    />
  </>
);

const SkeletonSection = () => (
  <section className="mt-16 animate-pulse">
    <header className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-brand-bgOff2 shrink-0" />
      <div className="flex-1">
        <div className="h-7 bg-brand-bgOff2 rounded w-1/3 mb-2" />
        <div className="h-3 bg-brand-bgOff2 rounded w-2/3" />
      </div>
    </header>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
      {[0, 1].map((i) => (
        <div key={i} className="bg-white border border-brand-border rounded-brandCard p-6">
          <div className="h-4 w-20 bg-brand-bgOff2 rounded-brandPill mb-4" />
          <div className="h-5 bg-brand-bgOff2 rounded w-3/4 mb-3" />
          <div className="h-3 bg-brand-bgOff2 rounded mb-2" />
          <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
        </div>
      ))}
    </div>
  </section>
);

const ResourcesLibrary = () => {
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [byCategory, setByCategory] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('resources')
        .select('*')
        .eq('domain', 'wms')
        .eq('is_published', true)
        .order('category')
        .order('sequence_number')
        .execute();

      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[ResourcesLibrary] load failed:', error);
        setStatus('error');
        return;
      }

      const grouped = (data || []).reduce((acc, r) => {
        (acc[r.category] = acc[r.category] || []).push(r);
        return acc;
      }, {});
      setByCategory(grouped);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const orderedKeys = CATEGORY_ORDER.filter(
    (k) => byCategory[k] && byCategory[k].length > 0
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* Back link */}
        <div className="mt-10">
          <Link
            to="/domain/wms"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to WMS
          </Link>
        </div>

        {/* Hero */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Manhattan WMS
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Resources
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Twenty-five curated resources across documentation, tools,
            community, certifications, and reference reading. Hand-picked
            from twenty-two years of practice.
          </p>
        </header>

        {/* Loading */}
        {status === 'loading' && (
          <>
            <SkeletonSection />
            <SkeletonSection />
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-8 max-w-xl mx-auto">
            <p className="text-base text-brand-text font-semibold">
              Couldn't load resources.
            </p>
            <p className="mt-2 text-sm text-brand-muted">
              Please refresh the page and try again.
            </p>
          </div>
        )}

        {/* Empty */}
        {status === 'ok' && orderedKeys.length === 0 && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-10 max-w-2xl mx-auto">
            <div className="text-4xl mb-3" aria-hidden="true">📚</div>
            <p className="text-lg text-brand-text font-semibold">
              Resource library coming soon.
            </p>
            <p className="mt-2 text-sm text-brand-muted leading-relaxed">
              Curated WMS resources are being assembled — check back shortly.
            </p>
          </div>
        )}

        {/* Loaded */}
        {status === 'ok' &&
          orderedKeys.map((key) => (
            <ResourceCategorySection
              key={key}
              categoryKey={key}
              resources={byCategory[key]}
            />
          ))}

        <ShowcaseSiblingNav currentPath={location.pathname} />

      </div>

      <PublicFooter />
    </div>
  );
};

export default ResourcesLibrary;
